import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { createUseStyles } from "react-jss";
import { useSwipeable } from "react-swipeable";
import { useAtom } from "jotai";
import { hapticTap } from "../helpers/haptics";
import { answeredState } from "../helpers/recoil";
import { CardType } from "../helpers/types";
import AnswerButtons from "./AnswerButtons";
import CardDetails from "./CardDetails";
import StudyCardSlide from "./StudyCardSlide";
import type { SlideDirection } from "./StudyCardSlide";

const CARD_SURFACES = {
  unflipped: {
    face: "#2c3139",
    border: "#404754"
  },
  flipped: {
    face: "#4a627a",
    border: "#7a94b8"
  }
};

const useStyles = createUseStyles({
  cardShellWrapper: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0
  },
  cardShell: {
    border: "1px solid",
    borderRadius: 12,
    boxSizing: "border-box",
    margin: "auto",
    width: "100%",
    minHeight: 140,
    height: "100%",
    backgroundColor: "transparent",
    transition: "border-color 0.55s ease, box-shadow 0.55s ease",
    touchAction: "pan-y"
  },
  cardShellMobile: {
    minHeight: 0,
    flex: 1
  },
  cardShellCompact: {
    minHeight: 96,
    height: "auto"
  },
  cardShellUnflipped: {
    borderColor: CARD_SURFACES.unflipped.border,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.24)"
  },
  cardShellFlipped: {
    borderColor: CARD_SURFACES.flipped.border,
    boxShadow: "0 0 0 1px rgba(122, 148, 184, 0.5), 0 2px 10px rgba(0, 0, 0, 0.32)"
  },
  cardScene: {
    perspective: 1000,
    width: "100%",
    minHeight: 140,
    height: "100%"
  },
  cardSceneMobile: {
    minHeight: 0
  },
  cardSceneCompact: {
    minHeight: 96,
    height: "auto"
  },
  card: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    transformStyle: "preserve-3d",
    transition: "transform 0.55s cubic-bezier(0.4, 0.2, 0.2, 1)",
    transform: "rotateY(0deg)",
    width: "100%",
    minHeight: 140,
    height: "100%"
  },
  cardShellCorrect: {
    borderColor: "#0F6E56",
    boxShadow: "0 0 0 2px rgba(15, 110, 86, 0.55), 0 0 18px rgba(15, 110, 86, 0.35)"
  },
  cardShellWrong: {
    borderColor: "#993C1D",
    boxShadow: "0 0 0 2px rgba(153, 60, 29, 0.55), 0 0 18px rgba(153, 60, 29, 0.35)"
  },
  statusBadge: {
    position: "absolute",
    top: 8,
    right: 10,
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    borderRadius: "50%",
    pointerEvents: "none",
    "& svg": {
      fontSize: 13
    }
  },
  statusBadgeCorrect: {
    backgroundColor: "#E1F5EE",
    color: "#0F6E56",
    boxShadow: "0 0 0 1px #0F6E56"
  },
  statusBadgeWrong: {
    backgroundColor: "#FAECE7",
    color: "#993C1D",
    boxShadow: "0 0 0 1px #993C1D"
  },
  cardContainer: {
    width: "100%",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    borderRadius: 12,
    height: "100%",
    minHeight: 0,
    "&:focus": {
      outline: "none"
    },
    "&:focus-visible": {
      outline: "2px solid #185FA5",
      outlineOffset: "2px",
      borderRadius: 12
    }
  },
  cardContainerMobile: {
    gap: 8,
    overflow: "hidden"
  },
  cardContainerCompact: {
    gap: 6
  },
  cardShellTappable: {
    cursor: "pointer"
  },
  swipeHint: {
    flexShrink: 0,
    textAlign: "center",
    fontSize: 11,
    color: "#64748b",
    margin: 0
  },
  flipCard: {
    transform: "rotateY(180deg)"
  }
});

const MARK_FEEDBACK_DELAY_MS = 450;

export const Flashcard = ({
  order,
  detailsOptions,
  isFlippedCount,
  isRevealedCount,
  showClearButton,
  cardDetails,
  isMobile = false,
  isCompact = false,
  onAnswerMarked,
  slidePhase = "idle",
  slideDirection = "forward",
  transitionEnabled = true,
  hasSwipeNavigation = false,
  nav,
  onFlippedChange
}: CardType & {
  isMobile?: boolean;
  isCompact?: boolean;
  onAnswerMarked?: () => void;
  slidePhase?: "idle" | "exit" | "enter";
  slideDirection?: SlideDirection;
  transitionEnabled?: boolean;
  hasSwipeNavigation?: boolean;
  nav?: ReactNode;
  onFlippedChange?: (order: number, flipped: boolean) => void;
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const skipFlipCount = useRef(true);
  const skipRevealCount = useRef(true);
  const [answeredList, setAnsweredList] = useAtom(answeredState);
  const [answer, setAnswer] = useState(answeredList.find((a) => a.order === order)?.answer);
  const advanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markAnswer = (value: boolean) => {
    hapticTap();
    setAnswer(value);
    if (!onAnswerMarked) {
      return;
    }

    if (advanceTimeoutRef.current) {
      clearTimeout(advanceTimeoutRef.current);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        advanceTimeoutRef.current = setTimeout(() => {
          onAnswerMarked();
          advanceTimeoutRef.current = null;
        }, MARK_FEEDBACK_DELAY_MS);
      });
    });
  };

  useEffect(
    () => () => {
      if (advanceTimeoutRef.current) {
        clearTimeout(advanceTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const answeredValue = answeredList.find((a) => a.order === order)?.answer;
    if (answer === null) {
      return setAnsweredList(answeredList.filter((item) => item?.order !== order));
    }
    if (answer !== answeredValue) {
      return setAnsweredList(
        answeredValue === undefined
          ? [...answeredList, { order, answer }]
          : [...answeredList.filter((x) => x.order !== order), { order, answer }]
      );
    }
  }, [answer]);

  useEffect(() => {
    setAnswer(answeredList.find((a) => a.order === order)?.answer);
  }, [answeredList]);

  useEffect(() => {
    if (skipFlipCount.current) {
      skipFlipCount.current = false;
      return;
    }
    setIsFlipped(true);
  }, [isFlippedCount]);

  useEffect(() => {
    if (skipRevealCount.current) {
      skipRevealCount.current = false;
      return;
    }
    setIsFlipped(false);
  }, [isRevealedCount]);

  useEffect(() => {
    onFlippedChange?.(order, isFlipped);
  }, [isFlipped, onFlippedChange, order]);

  const handleFlip = () => {
    hapticTap();
    setIsFlipped((flipped) => !flipped);
  };

  const handleCardPress = () => {
    if (window.getSelection()?.toString()) {
      return;
    }
    handleFlip();
  };

  const handlers = useSwipeable({
    onTap: () => handleCardPress(),
    onSwiped: (eventData) => {
      const absX = Math.abs(eventData.deltaX);
      const absY = Math.abs(eventData.deltaY);
      if (absX < 40 && absY < 40) {
        return;
      }
      if (eventData.dir === "Up" && showClearButton && absY > absX) {
        hapticTap();
        setAnswer(null);
      }
    },
    preventScrollOnSwipe: true,
    trackMouse: false
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        handleFlip();
        break;
      case "ArrowLeft":
        event.preventDefault();
        markAnswer(false);
        break;
      case "ArrowRight":
        event.preventDefault();
        markAnswer(true);
        break;
      case "ArrowUp":
      case "ArrowDown":
        if (showClearButton) {
          event.preventDefault();
          setAnswer(null);
        }
        break;
      default:
        break;
    }
  };

  const classes = useStyles();
  const isFullMobile = isMobile && !isCompact;
  const ariaLabel = isFullMobile
    ? hasSwipeNavigation
      ? `Flashcard ${order + 1}. Tap to flip. Swipe left for next card, right for previous.`
      : showClearButton
        ? `Flashcard ${order + 1}. Tap to flip. Swipe up to skip.`
        : `Flashcard ${order + 1}. Tap to flip.`
    : showClearButton
      ? `Flashcard ${order + 1}. Click or press Enter/Space to flip. Left arrow for wrong, Right for correct, Up/Down to clear.`
      : `Flashcard ${order + 1}. Click or press Enter/Space to flip. Left arrow for wrong, Right for correct.`;

  const cardFace = (
    <>
      {answer === true && (
        <span className={`${classes.statusBadge} ${classes.statusBadgeCorrect}`} aria-hidden="true">
          <CheckIcon fontSize="small" />
        </span>
      )}
      {answer === false && (
        <span className={`${classes.statusBadge} ${classes.statusBadgeWrong}`} aria-hidden="true">
          <ClearIcon fontSize="small" />
        </span>
      )}
      <div
        className={`${classes.cardShell} ${classes.cardShellTappable} ${
          isCompact ? classes.cardShellCompact : isFullMobile ? classes.cardShellMobile : ""
        } ${
          isFlipped ? classes.cardShellFlipped : classes.cardShellUnflipped
        } ${answer === true && classes.cardShellCorrect} ${answer === false && classes.cardShellWrong}`}
        onClick={isFullMobile ? undefined : handleCardPress}
        {...(isFullMobile ? handlers : {})}>
        <div
          className={`${classes.cardScene} ${
            isCompact ? classes.cardSceneCompact : isFullMobile ? classes.cardSceneMobile : ""
          }`}>
          <div className={`${classes.card} ${isFlipped && classes.flipCard}`}>
            <CardDetails
              cardDetails={cardDetails}
              currentSide="front"
              detailsOptions={detailsOptions}
              isMobile={isMobile}
              isCompact={isCompact}
            />
            <CardDetails
              cardDetails={cardDetails}
              currentSide="back"
              detailsOptions={detailsOptions}
              isMobile={isMobile}
              isCompact={isCompact}
            />
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div
      className={`${classes.cardContainer} ${
        isCompact ? classes.cardContainerCompact : isFullMobile ? classes.cardContainerMobile : ""
      }`}
      tabIndex={0}
      role="group"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}>
      <div className={classes.cardShellWrapper}>
        {onAnswerMarked ? (
          <StudyCardSlide
            slidePhase={slidePhase}
            slideDirection={slideDirection}
            transitionEnabled={transitionEnabled}>
            {cardFace}
          </StudyCardSlide>
        ) : (
          cardFace
        )}
      </div>
      {isFullMobile && (
        <p className={classes.swipeHint}>
          {hasSwipeNavigation
            ? "Tap to flip · Swipe ← next · → previous"
            : showClearButton
              ? "Tap to flip · ↑ skip"
              : "Tap to flip"}
        </p>
      )}
      {nav}
      <AnswerButtons
        answer={answer}
        setAnswer={setAnswer}
        showClearButton={showClearButton}
        variant={isFullMobile ? "bottomBar" : "inline"}
        onMarkWrong={() => markAnswer(false)}
        onMarkCorrect={() => markAnswer(true)}
      />
    </div>
  );
};

export default Flashcard;
